/**
 * Phase 326l — Activation Funnel tile for the Super Admin dashboard.
 *
 * Three signals on a single card:
 *   1. % of academies fully set up   (progress ring + raw counts)
 *   2. Most-missed milestones        (worst-first list with miss %)
 *   3. Stuck academies leaderboard   (>7 days verified, still missing items)
 *
 * Self-loads on mount, fails silently if the endpoint 403s / errors so a
 * misconfigured pod never breaks the rest of the dashboard layout.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertOctagon, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

export default function ActivationFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/super-admin/activation-funnel')
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.status || 'unknown');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card data-testid="activation-funnel-card-loading">
        <CardContent className="p-5">
          <Skeleton className="h-6 w-48 mb-4" />
          <div className="grid lg:grid-cols-3 gap-4">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Endpoint failed → fail-silent so dashboard stays usable.
  if (error || !data) return null;

  const { totals, milestone_miss_rates: misses = [], stuck_academies: stuck = [], stuck_days_threshold = 7 } = data;
  const pct = totals?.pct_fully_setup ?? 0;

  return (
    <Card
      className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white"
      data-testid="activation-funnel-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            Activation Funnel
          </span>
          <Badge variant="outline" className="text-[10px] tracking-wider">
            DAILY 10:00 IST CRON
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* ─────────────  COL 1 — Funnel summary  ───────────── */}
          <div
            className="p-4 rounded-xl bg-white border border-emerald-200"
            data-testid="activation-funnel-summary"
          >
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs text-muted-foreground">Fully set up</span>
              <span className="text-2xl font-display font-semibold tabular-nums text-emerald-700">
                {pct}%
              </span>
            </div>
            <Progress value={pct} className="h-2 mb-3" />
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> {totals?.fully_setup ?? 0} ready
              </span>
              <span className="flex items-center gap-1 text-amber-700">
                <Clock className="h-3 w-3" /> {totals?.in_progress ?? 0} in progress
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              {totals?.verified_academies ?? 0} verified academies tracked.
            </p>
          </div>

          {/* ─────────────  COL 2 — Most-missed milestones  ───────────── */}
          <div
            className="p-4 rounded-xl bg-white border border-amber-200"
            data-testid="activation-funnel-misses"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-muted-foreground">Most-missed milestones</span>
              <AlertOctagon className="h-3.5 w-3.5 text-amber-600" />
            </div>
            {misses.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">All academies are fully set up. 🎉</p>
            ) : (
              <ul className="space-y-1.5">
                {misses.slice(0, 4).map((m) => (
                  <li
                    key={m.key}
                    className="flex items-center justify-between gap-2"
                    data-testid={`miss-rate-${m.key}`}
                  >
                    <span className="text-xs font-medium text-slate-700 truncate">{m.label}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500"
                          style={{ width: `${Math.min(m.miss_pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold tabular-nums text-amber-700 w-12 text-right">
                        {m.miss_pct}%
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ─────────────  COL 3 — Stuck academies leaderboard  ───────────── */}
          <div
            className="p-4 rounded-xl bg-white border border-red-200"
            data-testid="activation-funnel-stuck"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-muted-foreground">
                Stuck &gt;{stuck_days_threshold}d
              </span>
              <Clock className="h-3.5 w-3.5 text-red-500" />
            </div>
            {stuck.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Nobody is stuck — clean funnel. 🚀</p>
            ) : (
              <ul className="space-y-1.5">
                {stuck.slice(0, 5).map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center justify-between gap-2"
                    data-testid={`stuck-row-${row.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/super-admin/academies?search=${encodeURIComponent(row.name || '')}`}
                        className="text-xs font-medium text-slate-800 hover:text-emerald-700 truncate block"
                      >
                        {row.name}
                      </Link>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {row.missing_count} item{row.missing_count === 1 ? '' : 's'} missing
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {row.days_since_verified}d
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            {stuck.length > 0 && (
              <Link
                to="/super-admin/academies"
                className="text-[11px] text-emerald-700 hover:text-emerald-900 flex items-center gap-1 mt-3 font-medium"
              >
                View all academies
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
